/**
 * Item 280 — switch a sound-detection class on or off, and set its threshold, for every camera
 * in a group. Superusers only (the rule of the per-camera sound settings; the Groups page shows
 * the button to superusers and the server refuses everyone else).
 *
 * Modelled on GroupDetectionDialog (item 266): the choice is first sent as a dry run and the
 * dialog shows its counts; Apply repeats the same call for real. A one-time action on the
 * cameras that are in the group now — a camera added later is not changed. Sound is analysed
 * from videos only.
 */
import * as Dialog from '@radix-ui/react-dialog'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cameraGroupsApi } from '@/api/endpoints'
import { queryKeys } from '@/config/queryConfig'
import { toast } from '@/lib/toast'
import type { CameraGroup, GroupAudioClassChange } from '@/types'

type Action = 'on' | 'off'
const ACTIONS: Action[] = ['on', 'off']
/** keep = each camera's own threshold; set = the same value on every camera; default = clear overrides. */
type ThresholdMode = 'thresholdKeep' | 'thresholdSet' | 'thresholdDefault'
const THRESHOLD_MODES: ThresholdMode[] = ['thresholdKeep', 'thresholdSet', 'thresholdDefault']

interface Props {
  group: CameraGroup
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** "40" -> 0.4; anything that is not a whole number 0–100 -> null. */
function percentToFraction(text: string): number | null {
  if (!/^\d{1,3}$/.test(text.trim())) return null
  const n = Number(text)
  return n >= 0 && n <= 100 ? n / 100 : null
}

export function GroupAudioDialog({ group, open, onOpenChange }: Props) {
  const { t } = useTranslation('groups')
  const { t: tc } = useTranslation('common')
  const qc = useQueryClient()
  const [classId, setClassId] = useState<number | null>(null)
  const [action, setAction] = useState<Action>('on')
  const [mode, setMode] = useState<ThresholdMode>('thresholdKeep')
  // The percentage the user typed; null until "set" is first chosen (then the class default).
  const [percent, setPercent] = useState<string | null>(null)

  const classes = useQuery({
    queryKey: queryKeys.cameraGroups.audioClasses(group.id),
    queryFn: ({ signal }) => cameraGroupsApi.getAudioClasses(group.id, signal),
    enabled: open,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })
  const list = classes.data?.classes ?? []
  const audioClass = list.find((c) => c.id === classId) ?? list[0]
  const enabled = action === 'on'
  const defaultPercent = audioClass ? String(Math.round(audioClass.global_threshold * 100)) : ''
  const percentText = percent ?? defaultPercent
  const fraction = mode === 'thresholdSet' ? percentToFraction(percentText) : null
  const percentInvalid = mode === 'thresholdSet' && fraction === null
  // undefined = leave the field out of the request (keep); null = clear; number = set.
  const threshold: number | null | undefined =
    mode === 'thresholdKeep' ? undefined : mode === 'thresholdDefault' ? null : fraction ?? undefined

  const change = (): GroupAudioClassChange => ({
    audio_class_id: audioClass!.id,
    enabled,
    ...(threshold !== undefined ? { threshold_override: threshold } : {}),
  })

  const preview = useQuery({
    queryKey: queryKeys.cameraGroups.audioClassPreview(group.id, audioClass?.id ?? 0, enabled, threshold),
    queryFn: ({ signal }) => cameraGroupsApi.setAudioClass(group.id, change(), true, signal),
    enabled: open && !!audioClass && !percentInvalid,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })

  const apply = useMutation({
    mutationFn: () => cameraGroupsApi.setAudioClass(group.id, change(), false),
    onSuccess: (r) => {
      toast.success(t('audio.applied', { count: r.updated.length, class: r.audio_class.class_name }))
      qc.invalidateQueries({ queryKey: ['cameras'] })
      qc.invalidateQueries({ queryKey: queryKeys.cameraGroups.all() })
      // The camera's own Sound settings keep their list under this key.
      qc.invalidateQueries({ queryKey: ['camera-audio-classes'] })
      onOpenChange(false)
    },
    onError: () => toast.error(t('audio.failed')),
  })

  const counts = preview.data
  const canApply = !!audioClass && !percentInvalid && !!counts && !preview.isFetching
    && counts.updated.length > 0 && !apply.isPending

  return (
    <Dialog.Root open={open} onOpenChange={apply.isPending ? undefined : onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Dialog.Title className="text-lg font-semibold">{t('audio.title', { name: group.name })}</Dialog.Title>
              <Dialog.Description className="text-sm text-muted-foreground">{t('audio.description')}</Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button type="button" aria-label={tc('actions.close')} disabled={apply.isPending}
                className="rounded-md p-1 text-muted-foreground hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          {classes.isError ? (
            <p className="mt-4 text-sm text-destructive">{t('audio.loadFailed')}</p>
          ) : !classes.data ? (
            <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />{t('audio.loading')}
            </p>
          ) : !audioClass ? (
            <p className="mt-4 text-sm text-muted-foreground">{t('audio.noClasses')}</p>
          ) : (
            <>
              <div className="mt-4 space-y-1.5">
                <label className="text-sm font-medium" htmlFor="group-audio-class">{t('audio.classLabel')}</label>
                <Select value={String(audioClass.id)} onValueChange={(v) => { setClassId(Number(v)); setPercent(null) }} disabled={apply.isPending}>
                  <SelectTrigger id="group-audio-class">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {list.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{t('audio.classOption', { name: c.class_name, model: c.model_name })}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t('audio.status', { on: audioClass.enabled_count, total: classes.data.group.member_count })}
                </p>
              </div>

              <div className="mt-4 space-y-1.5">
                <label className="text-sm font-medium" htmlFor="group-audio-action">{t('audio.actionLabel')}</label>
                <Select value={action} onValueChange={(v) => setAction(v as Action)} disabled={apply.isPending}>
                  <SelectTrigger id="group-audio-action">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIONS.map((a) => (
                      <SelectItem key={a} value={a}>{t(`audio.${a}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-4 space-y-1.5">
                <label className="text-sm font-medium" htmlFor="group-audio-threshold-mode">{t('audio.thresholdLabel')}</label>
                <Select value={mode} onValueChange={(v) => setMode(v as ThresholdMode)} disabled={apply.isPending}>
                  <SelectTrigger id="group-audio-threshold-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {THRESHOLD_MODES.map((m) => (
                      <SelectItem key={m} value={m}>{t(`audio.${m}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {mode === 'thresholdSet' && (
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground" htmlFor="group-audio-threshold">{t('audio.thresholdPercent')}</label>
                    <Input
                      id="group-audio-threshold"
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={100}
                      step={1}
                      value={percentText}
                      onChange={(e) => setPercent(e.target.value)}
                      disabled={apply.isPending}
                      aria-invalid={percentInvalid || undefined}
                    />
                    <p className="text-xs text-muted-foreground">{t('audio.thresholdHint', { percent: defaultPercent })}</p>
                  </div>
                )}
              </div>

              <div className="mt-4 min-h-[4.5rem] rounded-lg bg-muted/50 p-3 text-sm" aria-live="polite">
                {percentInvalid ? (
                  <p className="text-destructive">{t('audio.thresholdRange')}</p>
                ) : preview.isError ? (
                  <p className="text-destructive">{t('audio.previewFailed')}</p>
                ) : !counts || preview.isFetching ? (
                  <p className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />{t('audio.checking')}
                  </p>
                ) : (
                  <ul className="space-y-0.5">
                    <li className="font-medium">{t('audio.willChange', { count: counts.updated.length })}</li>
                    {counts.unchanged.length > 0 && (
                      <li className="text-muted-foreground">{t('audio.alreadySet', { count: counts.unchanged.length })}</li>
                    )}
                  </ul>
                )}
              </div>
            </>
          )}

          <div className="mt-6 flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} disabled={apply.isPending}>
              {tc('actions.cancel')}
            </Button>
            <Button className="flex-1" onClick={() => apply.mutate()} disabled={!canApply}>
              {apply.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('audio.apply')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
